<template>
    <transition appear enter-active-class="animated bounceInRight">
        <f7-view main>
            <f7-page :page-content="false">
                <cm-navbar :title="$ml.get('wasteOutOrders')">
                    <f7-nav-right>
                        <f7-link @click="refresh"><i class="f7-icons">refresh</i></f7-link>
                    </f7-nav-right>
                </cm-navbar>

                <f7-toolbar tabbar bottom>
                    <f7-link tab-link="#waste-out-create" tab-link-active>{{ $ml.get('wasteOutOrders') }}</f7-link>
                    <f7-link tab-link="#waste-out-recent">{{ $ml.get('wasteOutRecent') }}</f7-link>
                </f7-toolbar>

                <f7-tabs animated>
                    <f7-tab id="waste-out-create" class="page-content" tab-active>
                        <template v-if="canCreate">
                        <f7-block-title>{{ $ml.get('wasteOutChooseTo') }}</f7-block-title>
                        <f7-list inline-labels no-hairlines-md>
                            <f7-list-input
                                type="text"
                                :label="$ml.get('wasteOutSearch')"
                                :placeholder="$ml.get('wasteOutSearchTo')"
                                clear-button
                                :value="toSearch"
                                @input="toSearch = $event.target.value"
                                @keyup.enter="loadTos"
                            ></f7-list-input>
                        </f7-list>
                        <f7-block>
                            <f7-button fill @click="loadTos" :disabled="loadingTos">
                                {{ $ml.get('wasteOutSearch') }}
                            </f7-button>
                        </f7-block>

                        <f7-block v-if="loadingTos" class="text-align-center">
                            <f7-preloader></f7-preloader>
                        </f7-block>
                        <f7-list media-list v-else>
                            <f7-list-item
                                v-for="item in tos"
                                :key="item.id"
                                :title="toLabel(item)"
                                :subtitle="toSubtitle(item)"
                                :text="toTargetContainers(item)"
                                :after="item.created_at || ''"
                                class="waste-out-to-item"
                                @click="selectTo(item)"
                            ></f7-list-item>
                        </f7-list>
                        <f7-card v-if="!loadingTos && !tos.length" :content="$ml.get('noRecords')"></f7-card>
                        </template>
                        <f7-block v-else class="waste-out-permission-message">
                            {{ $ml.get('wasteOutNoCreatePermission') }}
                        </f7-block>
                    </f7-tab>

                    <f7-tab id="waste-out-recent" class="page-content">
                        <f7-block-title>{{ $ml.get('wasteOutRecent') }}</f7-block-title>
                        <f7-list inline-labels no-hairlines-md>
                            <f7-list-input
                                type="text"
                                :label="$ml.get('wasteOutSearch')"
                                :placeholder="$ml.get('wasteOutSearch')"
                                clear-button
                                :value="wasteOutSearch"
                                @input="wasteOutSearch = $event.target.value"
                                @keyup.enter="loadWasteOuts"
                            ></f7-list-input>
                            <f7-list-input
                                type="date"
                                :label="$ml.get('fromDate')"
                                :value="fromDate"
                                @input="fromDate = $event.target.value"
                            ></f7-list-input>
                            <f7-list-input
                                type="date"
                                :label="$ml.get('toDate')"
                                :value="toDate"
                                @input="toDate = $event.target.value"
                            ></f7-list-input>
                        </f7-list>
                        <f7-block>
                            <f7-button fill @click="loadWasteOuts" :disabled="loadingWasteOuts">
                                {{ $ml.get('wasteOutSearch') }}
                            </f7-button>
                        </f7-block>

                        <f7-block v-if="loadingWasteOuts" class="text-align-center">
                            <f7-preloader></f7-preloader>
                        </f7-block>
                        <template v-else>
                            <f7-card v-for="item in wasteOuts" :key="item.id" class="waste-out-receipt">
                                <f7-card-header>
                                    <span>{{ item.code || ('#' + item.id) }}</span>
                                    <span :class="receiptStatusClass(item)">{{ receiptStatus(item) }}</span>
                                </f7-card-header>
                                <f7-card-content>
                                    <p>{{ $ml.get('wasteOutTo') }}: {{ receiptToLabel(item) }}</p>
                                    <p>{{ $ml.get('containers') }}: {{ item.container ? item.container.name : '' }}</p>
                                    <p>{{ $ml.get('wasteOutOutputMaterial') }}: {{ materialName(item.material || (item.container && item.container.material)) }}</p>
                                    <p>{{ $ml.get('wasteOutAmount') }}: {{ amountText(item.amount) }}</p>
                                    <p>{{ $ml.get('wasteOutNote') }}: {{ item.note }}</p>
                                    <p v-if="item.createdBy">{{ $ml.get('wasteOutCreatedBy') }}: {{ item.createdBy.name }}</p>
                                    <p v-if="item.created_at">{{ item.created_at }}</p>
                                    <p v-if="item.cancel_note">{{ $ml.get('wasteOutCancelNote') }}: {{ item.cancel_note }}</p>
                                </f7-card-content>
                                <f7-card-footer v-if="canCancelReceipt(item)">
                                    <span></span>
                                    <f7-link color="red" @click="askCancel(item)">{{ $ml.get('wasteOutCancel') }}</f7-link>
                                </f7-card-footer>
                            </f7-card>
                            <f7-card v-if="!wasteOuts.length" :content="$ml.get('noRecords')"></f7-card>
                        </template>
                    </f7-tab>
                </f7-tabs>

                <f7-popup
                    class="waste-out-popup"
                    :opened="popupOpened"
                    tablet-fullscreen
                    @popup:closed="closePopup"
                >
                    <f7-view>
                        <f7-page>
                            <f7-navbar :title="$ml.get('wasteOutOrders')">
                                <f7-nav-right>
                                    <f7-link popup-close>{{ $ml.get('close') }}</f7-link>
                                </f7-nav-right>
                            </f7-navbar>

                            <template v-if="selectedTo">
                                <f7-block-title>{{ $ml.get('wasteOutSelectedTo') }}</f7-block-title>
                                <f7-list inline-labels no-hairlines-md>
                                    <f7-list-item :title="$ml.get('wasteOutTo')" :after="toLabel(selectedTo)"></f7-list-item>
                                    <f7-list-item :title="$ml.get('wasteOutOutputMaterial')" :after="materialName(selectedTo.tmaterial)"></f7-list-item>
                                    <f7-list-item :title="$ml.get('wasteOutOutputAmount')" :after="amountText(selectedTo.to_amount)"></f7-list-item>
                                </f7-list>

                                <f7-block-title>{{ $ml.get('wasteOutTargetContainer') }}</f7-block-title>
                                <f7-list v-if="targetContainers.length">
                                    <f7-list-item
                                        v-for="container in targetContainers"
                                        :key="container.id"
                                        radio
                                        name="waste-out-target-container"
                                        :value="container.id"
                                        :title="container.name"
                                        :after="amountText(container.capacity)"
                                        :checked="isSelectedContainer(container)"
                                        @change="selectContainer(container)"
                                    ></f7-list-item>
                                </f7-list>
                                <f7-card v-else :content="$ml.get('wasteOutNoTargetContainers')"></f7-card>

                                <template v-if="selectedContainer">
                                    <f7-block-title>{{ $ml.get('basicInfo') }}</f7-block-title>
                                    <f7-list inline-labels no-hairlines-md>
                                        <f7-list-item :title="$ml.get('containers')" :after="selectedContainer.name"></f7-list-item>
                                        <f7-list-item :title="$ml.get('wasteOutOutputMaterial')" :after="materialName(selectedMaterial)"></f7-list-item>
                                        <f7-list-item :title="$ml.get('wasteOutCurrentStock')" :after="amountText(currentStock)"></f7-list-item>
                                        <f7-list-input
                                            type="number"
                                            name="waste-out-amount"
                                            min="0"
                                            step="any"
                                            :label="$ml.get('wasteOutAmount')"
                                            placeholder="0"
                                            clear-button
                                            :value="form.amount"
                                            @input="form.amount = $event.target.value"
                                        ></f7-list-input>
                                        <f7-list-input
                                            type="textarea"
                                            name="waste-out-note"
                                            :label="$ml.get('wasteOutNote')"
                                            :placeholder="$ml.get('wasteOutNote')"
                                            clear-button
                                            :value="form.note"
                                            @input="form.note = $event.target.value"
                                        ></f7-list-input>
                                    </f7-list>
                                    <f7-block>
                                        <f7-row>
                                            <f7-col>
                                                <f7-button outline @click="fillAllRemaining" :disabled="currentStock <= 0">
                                                    {{ $ml.get('wasteOutAllRemaining') }}
                                                </f7-button>
                                            </f7-col>
                                            <f7-col>
                                                <f7-button fill @click="confirmCreate" :disabled="submitting">
                                                    {{ $ml.get('wasteOutCreate') }}
                                                </f7-button>
                                            </f7-col>
                                        </f7-row>
                                    </f7-block>
                                </template>
                            </template>
                        </f7-page>
                    </f7-view>
                </f7-popup>
            </f7-page>
        </f7-view>
    </transition>
</template>

<script>
    import CMNavbar from './Navbar'
    import Query    from '@/services/api/Query'
    import Mutation from '@/services/api/Mutation'

    export default {
        name: 'WasteOut',
        components: {
            'cm-navbar': CMNavbar,
        },
        data() {
            return {
                tos: [],
                wasteOuts: [],
                toSearch: '',
                wasteOutSearch: '',
                fromDate: '',
                toDate: '',
                selectedTo: null,
                selectedContainer: null,
                form: {
                    amount: '',
                    note: '',
                },
                loadingTos: false,
                loadingWasteOuts: false,
                submitting: false,
                popupOpened: false,
            }
        },
        computed: {
            targetContainers() {
                return this.selectedTo && Array.isArray(this.selectedTo.target_containers)
                    ? this.selectedTo.target_containers
                    : []
            },
            selectedMaterial() {
                if (!this.selectedContainer) return null

                // This document always represents the TO output material. The
                // API separately rejects a silo that has since been reused for
                // another material, rather than changing the document material.
                return this.selectedTo.tmaterial || this.selectedContainer.material || null
            },
            selectedMaterialId() {
                return this.selectedTo ? this.selectedTo.to_material : null
            },
            currentStock() {
                return this.toNumber(this.selectedContainer ? this.selectedContainer.capacity : 0)
            },
            canCreate() {
                return this.$helper.hasPermission('waste_out_create')
            },
            canCancel() {
                return this.$helper.hasPermission('waste_out_cancel')
            },
        },
        methods: {
            toNumber(value) {
                let number = Number(value)

                return Number.isFinite(number) ? number : 0
            },

            amountText(value) {
                return this.$helper.numFormat(this.toNumber(value)) + ' kg'
            },

            materialName(material) {
                return material && material.name ? material.name : ''
            },

            toLabel(item) {
                if (!item) return ''

                return item.code || ('TO #' + item.id)
            },

            toSubtitle(item) {
                let material = this.materialName(item.tmaterial)
                let amount = this.amountText(item.to_amount)

                return material ? material + ' · ' + amount : amount
            },

            toTargetContainers(item) {
                let containers = Array.isArray(item.target_containers) ? item.target_containers : []

                return containers.map(container => container.name).join(', ')
            },

            isSelectedContainer(container) {
                return this.selectedContainer && String(this.selectedContainer.id) === String(container.id)
            },

            selectTo(item) {
                this.selectedTo = item
                let containers = Array.isArray(item.target_containers) ? item.target_containers : []

                // A machine-run TO normally has one output silo. Select it so
                // the disposal form/action appears immediately after choosing
                // the TO. Multiple output silos still require an explicit pick.
                this.selectedContainer = containers.length === 1 ? containers[0] : null
                this.form.amount = ''
                this.form.note = ''
                this.popupOpened = true
            },

            resetCreateForm() {
                this.selectedTo = null
                this.selectedContainer = null
                this.form.amount = ''
                this.form.note = ''
            },

            closePopup() {
                this.popupOpened = false
                this.resetCreateForm()
            },

            selectContainer(container) {
                this.selectedContainer = container
                this.form.amount = ''
            },

            fillAllRemaining() {
                this.form.amount = String(this.currentStock)
            },

            canCancelReceipt(item) {
                return this.canCancel && item && item.can_cancel === true
            },

            receiptStatus(item) {
                return item.status_label || item.status || ''
            },

            receiptStatusClass(item) {
                return Number(item.status) === 2 || String(item.status || '').toLowerCase() === 'cancelled'
                    ? 'waste-out-status-cancelled'
                    : 'waste-out-status-approved'
            },

            receiptToLabel(item) {
                let toId = item.to_id || (item.to && item.to.id)

                return toId ? 'TO #' + toId : ''
            },

            errorMessage(error) {
                let errors = error && error.data && error.data.errors

                if (Array.isArray(errors) && errors.length)
                    return this.$helper.errors(errors)

                if (error && error.response && error.response.data && error.response.data.message)
                    return error.response.data.message

                return error && error.message ? error.message : this.$ml.get('errors.title')
            },

            showError(error) {
                return this.$f7.dialog.alert(this.errorMessage(error), this.$ml.get('errors.title'))
            },

            async loadTos() {
                if (!this.canCreate) return

                this.loadingTos = true
                try {
                    let result = await Query.withWasteOutTos(this.toSearch.trim(), 20).get()
                    if (result.data.errors) return this.showError(result)

                    this.tos = this.$helper.data(result, 'waste_out_tos')
                    if (this.selectedTo) {
                        let refreshedTo = this.tos.find(item => String(item.id) === String(this.selectedTo.id))
                        if (!refreshedTo) {
                            this.selectedTo = null
                            this.selectedContainer = null
                        } else {
                            this.selectedTo = refreshedTo
                            if (this.selectedContainer) {
                                this.selectedContainer = this.targetContainers.find(item => String(item.id) === String(this.selectedContainer.id)) || null
                            }
                        }
                    }
                } catch (error) {
                    this.showError(error)
                } finally {
                    this.loadingTos = false
                }
            },

            async loadWasteOuts() {
                this.loadingWasteOuts = true
                try {
                    let result = await Query.withWasteOuts(this.wasteOutSearch.trim(), 20, {
                        from_date: this.fromDate,
                        to_date: this.toDate,
                    }).get()
                    if (result.data.errors) return this.showError(result)

                    this.wasteOuts = this.$helper.data(result, 'waste_outs')
                } catch (error) {
                    this.showError(error)
                } finally {
                    this.loadingWasteOuts = false
                }
            },

            async refresh() {
                // Query is a legacy fluent builder with a shared query buffer.
                // Keep these requests sequential so their GraphQL bodies cannot mix.
                if (this.canCreate) await this.loadTos()
                await this.loadWasteOuts()
            },

            validateCreate() {
                let amount = this.toNumber(this.form.amount)
                let note = (this.form.note || '').trim()

                if (!this.selectedTo || !this.selectedContainer || !this.selectedMaterialId)
                    return false
                if (amount <= 0) {
                    this.$f7.dialog.alert(this.$ml.get('errors.greaterZero'), this.$ml.get('errors.title'))
                    return false
                }
                if (amount > this.currentStock) {
                    this.$f7.dialog.alert(this.$ml.get('errors.wasteOutAmountOverStock'), this.$ml.get('errors.title'))
                    return false
                }
                if (!note) {
                    this.$f7.dialog.alert(this.$ml.get('errors.wasteOutNoteRequired'), this.$ml.get('errors.title'))
                    return false
                }

                return true
            },

            confirmCreate() {
                if (!this.validateCreate()) return

                let amount = this.toNumber(this.form.amount)
                let before = this.currentStock
                let after = before - amount
                let text = '<p>' + this.$ml.get('wasteOutBefore') + ': <b>' + this.amountText(before) + '</b></p>' +
                    '<p>' + this.$ml.get('wasteOutAmount') + ': <b>' + this.amountText(amount) + '</b></p>' +
                    '<p>' + this.$ml.get('wasteOutAfter') + ': <b>' + this.amountText(after) + '</b></p>'

                this.$f7.dialog.confirm(text, this.$ml.get('wasteOutConfirm'), () => this.create())
            },

            async create() {
                if (this.submitting || !this.validateCreate()) return

                this.submitting = true
                this.$f7.dialog.preloader(this.$ml.get('processing'))
                let isPreloaderOpen = true
                try {
                    let result = await Mutation.createWasteOut({
                        to_id: this.selectedTo.id,
                        container_id: this.selectedContainer.id,
                        material_id: this.selectedMaterialId,
                        amount: this.toNumber(this.form.amount),
                        note: this.form.note.trim(),
                    }).execute()

                    if (result.data.errors) {
                        this.$f7.dialog.close()
                        isPreloaderOpen = false
                        return this.showError(result)
                    }

                    this.$f7.dialog.close()
                    isPreloaderOpen = false
                    this.closePopup()
                    await this.refresh()
                    this.$f7.dialog.alert(this.$ml.get('success.createWasteOut'), this.$ml.get('information'))
                } catch (error) {
                    if (isPreloaderOpen) this.$f7.dialog.close()
                    isPreloaderOpen = false
                    this.showError(error)
                } finally {
                    this.submitting = false
                    if (isPreloaderOpen) this.$f7.dialog.close()
                }
            },

            askCancel(item) {
                this.$f7.dialog.prompt(
                    this.$ml.get('wasteOutCancelNote'),
                    this.$ml.get('wasteOutCancel'),
                    (note) => {
                        if (!(note || '').trim()) {
                            return this.$f7.dialog.alert(
                                this.$ml.get('errors.wasteOutCancelNoteRequired'),
                                this.$ml.get('errors.title'),
                                () => this.askCancel(item)
                            )
                        }

                        return this.cancel(item, note.trim())
                    }
                )
            },

            async cancel(item, note) {
                this.$f7.dialog.preloader(this.$ml.get('processing'))
                let isPreloaderOpen = true
                try {
                    let result = await Mutation.cancelWasteOut({
                        id: item.id,
                        cancel_note: note,
                    }).execute()

                    if (result.data.errors) {
                        this.$f7.dialog.close()
                        isPreloaderOpen = false
                        return this.showError(result)
                    }

                    this.$f7.dialog.close()
                    isPreloaderOpen = false
                    await this.refresh()
                    this.$f7.dialog.alert(this.$ml.get('success.cancelWasteOut'), this.$ml.get('information'))
                } catch (error) {
                    if (isPreloaderOpen) this.$f7.dialog.close()
                    isPreloaderOpen = false
                    this.showError(error)
                } finally {
                    if (isPreloaderOpen) this.$f7.dialog.close()
                }
            },
        },
        async created() {
            await this.refresh()
        },
    }
</script>

<style scoped>
    .waste-out-to-item {
        cursor: pointer;
    }

    .waste-out-permission-message {
        color: #8e8e93;
        font-size: 13px;
    }

    .waste-out-receipt p {
        margin: 0 0 8px;
    }

    .waste-out-receipt p:last-child {
        margin-bottom: 0;
    }

    .waste-out-status-approved,
    .waste-out-status-cancelled {
        border-radius: 12px;
        color: #fff;
        font-size: 12px;
        padding: 3px 8px;
    }

    .waste-out-status-approved {
        background: #34c759;
    }

    .waste-out-status-cancelled {
        background: #8e8e93;
    }
</style>
